import { useLinksMutation } from "@src/data/mutations/links";
import type { Links } from "@src/types";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Button } from "@src/components/Button";
import { Download } from "@src/components/Download";
import { LoginButton } from "@src/components/LoginButton";
import { FormGroup } from "@src/components/FormGroup";
import { Input } from "@src/components/Input";
import { Loader } from "@src/components/Loader";
import { PortalDesktop } from "@src/components/PortalDesktop";
import { PortalMobile } from "@src/components/PortalMobile";
import { Select } from "@src/components/Select";

import { useStateWithQueryString } from "@src/hooks/useStateWithQueryString";

import { formatCnpj } from "@src/utils/formatCnpj";
import { isMobile } from "@src/utils/isMobile";
import { onlyDigits } from "@src/utils/onlyDigits";

import "./style.less";

export function Portal() {
  const [findBy, setFindBy] = useStateWithQueryString("cnpj", "findBy");
  const [cnpj, setCnpj] = useStateWithQueryString("", "cnpj");
  const [name, setName] = useStateWithQueryString("", "name");

  const nameOrCnpj = findBy === "name" ? name : onlyDigits(cnpj);
  const getLinks = useLinksMutation(nameOrCnpj);
  const loading = getLinks.isPending;

  const [response, setResponse] = useState<Links>();
  const links = useMemo(() => response?.links || {}, [response?.links]);

  function submitForm() {
    getLinks.mutateAsync().then(setResponse, (error) => {
      console.log("[getLinks]", error);
    });
  }

  useLayoutEffect(() => {
    if (
      (findBy === "cnpj" && onlyDigits(cnpj).length === 14) ||
      (findBy === "name" && name.length > 0)
    ) {
      submitForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mantém o CNPJ na query string apenas quando o tipo é cnpj
  // Mantém o nome na query string apenas quando o tipo é name
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
    } else if (findBy === "cnpj" && name.length > 0) {
      setName("");
    } else if (findBy === "name" && cnpj.length > 0) {
      setCnpj("");
    }
  }, [findBy, cnpj, name, setCnpj, setName]);

  // Limpa os resultados automaticamente, quando necessário
  useEffect(() => {
    const hasLinks = Object.keys(links).length > 0;
    if (findBy === "cnpj" && onlyDigits(cnpj).length < 14 && hasLinks) {
      setResponse({ found_by: "cnpj", links: {} });
    } else if (findBy === "name" && name.length === 0 && hasLinks) {
      setResponse({ found_by: "name", links: {} });
    }
  }, [links, cnpj, name, findBy]);

  return (
    <div className="Portal-page">
      <LoginButton />
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          submitForm();
        }}
      >
        <fieldset disabled={loading}>
          <FormGroup>
            <label htmlFor="findBy">Procurar por</label>
            <Select
              id="findBy"
              name="findBy"
              value={findBy}
              onChange={(event) =>
                setFindBy(event.target.value as Links["found_by"])
              }
              title="Procurar por"
              required
            >
              <option value="cnpj">CNPJ</option>
              <option value="name">Nome da rádio</option>
            </Select>
          </FormGroup>

          {findBy === "name" && (
            <FormGroup>
              <label htmlFor="name">Nome da rádio</label>
              <Input
                id="name"
                type="text"
                name="name"
                value={name}
                onChange={(event) => {
                  setName(event.currentTarget.value);
                }}
                required
              />
            </FormGroup>
          )}

          {findBy === "cnpj" && (
            <FormGroup>
              <label htmlFor="cnpj">CNPJ</label>
              <Input
                id="cnpj"
                type="text"
                name="cnpj"
                autoComplete="username"
                value={formatCnpj(cnpj)}
                onChange={(event) => {
                  setCnpj(onlyDigits(event.currentTarget.value));
                }}
                required
              />
            </FormGroup>
          )}

          <Button type="submit" disabled={loading}>
            Procurar links
          </Button>
          {getLinks.isError && (
            <span className="error">{getLinks.error.message}</span>
          )}
          <Loader visible={loading} />
        </fieldset>
      </form>
      {isMobile ? (
        <PortalMobile links={links} />
      ) : (
        <>
          <PortalDesktop links={links} />
          <Download />
        </>
      )}
    </div>
  );
}
